import { Link } from 'react-router';
import { IconCalendarEvent, IconX } from '@tabler/icons-react';
import { PractitionerInput } from '@/components/PractitionerInput';
import { usePractitionersByRoles } from '@/hooks/usePractitionersByRoles';
import { IvrDigit, IvrHoursSet, IvrKeyMapping, IvrTarget } from '@bariendo/lib/ivr';
import { ReactElement, useCallback, useMemo } from 'react';
import { Attachment, Practitioner } from '@medplum/fhirtypes';
import { createReference, resolveId } from '@medplum/core';
import { mapPractitionersToWithRole } from '@/pages/SchedulingPage/utils/schedulingShared';
import { IvrAudioFileInput } from './IvrAudioFileInput';
import { useIvrAudioPlaybackUrl, useIvrAudioUpload } from './ivrAudio';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IvrHoursSetId, IvrTargetKind, IvrTargetScope } from '@bariendo/lib/constants';
import {
  buildRungWhoOptions,
  createTargetForWhoValue,
  isRoleTarget,
  nextFallbackTarget,
  rungRingSeconds,
  toRungWhoValue,
} from './ivrRungs';

/** Value used by the hours Select for "this option routes the same way around the clock". */
const ALWAYS_HOURS_VALUE = 'always';

/** Width of the `then` gutter, so a rung's own inputs line up under its control. */
const RUNG_GUTTER_WIDTH = 30;

const SCOPE_OPTIONS = [
  { value: IvrTargetScope.POOL, label: 'Everyone in the pool' },
  { value: IvrTargetScope.OWNER, label: 'Individual owner' },
];

interface IVRKeyMappingRowProps {
  digit: IvrDigit;
  mapping: IvrKeyMapping | undefined;
  hoursSets: IvrHoursSet[];
  onChange: (digit: IvrDigit, mapping: IvrKeyMapping | undefined) => void;
  disabled: boolean;
  languageSwitchKey: number;
}

interface RungInputProps {
  target: IvrTarget;
  disabled: boolean;
  onTargetChange: (target: IvrTarget) => void;
}

function ExternalNumberRungInput({ target, disabled, onTargetChange }: RungInputProps): ReactElement | null {
  if (target.kind !== IvrTargetKind.EXTERNAL_NUMBER) {
    return null;
  }

  return (
    <TextInput
      flex={1}
      miw={0}
      size="xs"
      type="tel"
      disabled={disabled}
      value={target.phoneNumber}
      aria-label="External number"
      placeholder="+1 555 000 0000"
      // The rung cannot be saved until this is filled in, so the empty state is marked rather
      // than captioned — the save guard names the digit if it is still empty.
      error={!target.phoneNumber.trim()}
      onChange={(event) =>
        onTargetChange({ kind: IvrTargetKind.EXTERNAL_NUMBER, phoneNumber: event.currentTarget.value })
      }
    />
  );
}

function PlayAndHangupRungInput({ target, disabled, onTargetChange }: RungInputProps): ReactElement | null {
  const audioFile = target.kind === IvrTargetKind.PLAY_AND_HANGUP ? target.audioFile : undefined;

  const handleAttachmentChange = useCallback(
    (attachment: Attachment | undefined) => {
      onTargetChange({ kind: IvrTargetKind.PLAY_AND_HANGUP, audioFile: attachment ?? {} });
    },
    [onTargetChange]
  );

  const { uploading, handleFileChange } = useIvrAudioUpload(handleAttachmentChange);
  const playbackUrl = useIvrAudioPlaybackUrl(audioFile);

  if (target.kind !== IvrTargetKind.PLAY_AND_HANGUP) {
    return null;
  }

  return (
    <Stack gap={4} flex={1} miw={0}>
      <IvrAudioFileInput
        size="xs"
        audioFile={audioFile}
        disabled={disabled}
        uploading={uploading}
        uploadPromptSuffix="audio"
        emptyDescription="Played before the call ends"
        onFileChange={handleFileChange}
        onRemove={() => handleAttachmentChange(undefined)}
      />
      {playbackUrl && <audio controls src={playbackUrl} style={{ width: '100%' }} />}
    </Stack>
  );
}

function SpecificProviderRungInput({ target, disabled, onTargetChange }: RungInputProps): ReactElement | null {
  const { practitioners, practitionerRoles, practitionersById, loading } = usePractitionersByRoles();

  const enrichedPractitioners = useMemo(
    () => mapPractitionersToWithRole(practitioners, practitionerRoles),
    [practitioners, practitionerRoles]
  );

  const providerRef = target.kind === IvrTargetKind.SPECIFIC_PROVIDER ? target.providerRef : undefined;

  const selectedPractitioner = useMemo(() => {
    const providerId = providerRef?.reference ? resolveId(providerRef) : undefined;
    return providerId ? (practitionersById.get(providerId) ?? null) : null;
  }, [providerRef, practitionersById]);

  if (target.kind !== IvrTargetKind.SPECIFIC_PROVIDER) {
    return null;
  }

  return (
    <>
      <PractitionerInput
        size="xs"
        flex={1}
        label=""
        required={false}
        clearable={true}
        value={selectedPractitioner}
        practitioners={enrichedPractitioners}
        disabled={disabled || loading}
        onChange={(resource: Practitioner | null) =>
          onTargetChange({
            kind: IvrTargetKind.SPECIFIC_PROVIDER,
            providerRef: resource ? createReference(resource) : {},
          })
        }
      />
    </>
  );
}

function OnCallNpRungInput({ target, disabled }: RungInputProps): ReactElement | null {
  if (target.kind !== IvrTargetKind.ON_CALL_NP) {
    return null;
  }

  return (
    <Button
      size="compact-xs"
      variant="subtle"
      component={Link}
      to="/on-call-schedule"
      leftSection={<IconCalendarEvent size={14} />}
      disabled={disabled}
    >
      Manage the on-call NP schedule
    </Button>
  );
}

/** Whatever the rung's chosen target needs before it can be dialled, inline under the rung. */
function RungInput(props: RungInputProps): ReactElement | null {
  switch (props.target.kind) {
    case IvrTargetKind.EXTERNAL_NUMBER:
      return <ExternalNumberRungInput {...props} />;
    case IvrTargetKind.PLAY_AND_HANGUP:
      return <PlayAndHangupRungInput {...props} />;
    case IvrTargetKind.SPECIFIC_PROVIDER:
      return <SpecificProviderRungInput {...props} />;
    case IvrTargetKind.ON_CALL_NP:
      return <OnCallNpRungInput {...props} />;
    default:
      return null;
  }
}

interface RungProps {
  target: IvrTarget | undefined;
  /** `First` / `then` on a chain with fallbacks; blank on a lone rung. Always reserved. */
  gutterLabel: string;
  disabled: boolean;
  removable: boolean;
  onTargetChange: (target: IvrTarget) => void;
  onRemove: () => void;
}

/**
 * One rung: who it rings, and — only for a team — which of them.
 *
 * Scope is a second control rather than a `[type] × [team]` cross product in the Who list
 * (BAR-2618): the cross product is ten-plus flat options that read as noise down a column,
 * and it is meaningless for a rota, an external number or a recording, none of which render
 * it here.
 */
function Rung({ target, gutterLabel, disabled, removable, onTargetChange, onRemove }: RungProps): ReactElement {
  const whoOptions = useMemo(() => buildRungWhoOptions(target), [target]);

  const handleWhoChange = (value: string | null) => {
    const next = value ? createTargetForWhoValue(value, target) : undefined;

    if (!next) {
      onRemove();
      return;
    }

    onTargetChange(next);
  };

  return (
    <Group gap="xs" wrap="nowrap" align="center">
      {/* Always reserved, even when blank, so every rung's Who control starts at
          the same x down the whole column. */}
      <Text size="xs" c="dimmed" w={RUNG_GUTTER_WIDTH} ta="right" style={{ flexShrink: 0 }}>
        {gutterLabel}
      </Text>
      <Select
        size="xs"
        flex={1}
        miw={0}
        clearable
        aria-label="Who"
        data={whoOptions}
        placeholder="Unassigned"
        clearButtonProps={{ 'aria-label': 'Unassign' }}
        value={toRungWhoValue(target)}
        disabled={disabled}
        onChange={handleWhoChange}
      />
      {isRoleTarget(target) && (
        <SegmentedControl
          size="xs"
          data={SCOPE_OPTIONS}
          value={target.scope}
          disabled={disabled}
          style={{ flexShrink: 0 }}
          onChange={(scope) => onTargetChange({ ...target, scope: scope as IvrTargetScope })}
        />
      )}
      {/* The kinds that need a resource take the same slot a team's scope does,
          rather than wrapping to a second line: a rung is one row. */}
      {target && <RungInput target={target} disabled={disabled} onTargetChange={onTargetChange} />}
      {target && (
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {rungRingSeconds(target)}s
        </Text>
      )}
      {removable && (
        <ActionIcon
          size="sm"
          color="gray"
          variant="subtle"
          disabled={disabled}
          aria-label="Remove fallback"
          style={{ flexShrink: 0 }}
          onClick={onRemove}
        >
          <IconX size={14} />
        </ActionIcon>
      )}
    </Group>
  );
}

interface RungChainProps {
  targets: IvrTarget[];
  disabled: boolean;
  onTargetsChange: (targets: IvrTarget[]) => void;
}

/**
 * The ordered list of rungs for one slot, walked top to bottom when nobody answers.
 *
 * A one-rung chain is deliberately indistinguishable from the single control this replaced —
 * no gutter, no remove button — so the common option is not made to look like a ladder.
 */
function RungChain({ targets, disabled, onTargetsChange }: RungChainProps): ReactElement {
  const replaceAt = (index: number, target: IvrTarget) =>
    onTargetsChange(targets.map((existing, position) => (position === index ? target : existing)));

  const removeAt = (index: number) => onTargetsChange(targets.filter((_existing, position) => position !== index));

  return (
    <Stack gap="xs">
      {targets.length === 0 ? (
        <Rung
          target={undefined}
          gutterLabel=""
          disabled={disabled}
          removable={false}
          onTargetChange={(target) => onTargetsChange([target])}
          onRemove={() => onTargetsChange([])}
        />
      ) : (
        targets.map((target, index) => (
          <Rung
            key={`${index}-${target.kind}`}
            target={target}
            gutterLabel={targets.length > 1 ? (index === 0 ? 'First' : 'then') : ''}
            disabled={disabled}
            removable={targets.length > 1}
            onTargetChange={(next) => replaceAt(index, next)}
            onRemove={() => removeAt(index)}
          />
        ))
      )}
      {targets.length > 0 && (
        <Button
          variant="subtle"
          size="compact-xs"
          disabled={disabled}
          onClick={() => onTargetsChange([...targets, nextFallbackTarget(targets)])}
        >
          + Add fallback
        </Button>
      )}
    </Stack>
  );
}

export default function IVRKeyMappingRow({
  digit,
  mapping,
  hoursSets,
  onChange,
  disabled,
  languageSwitchKey,
}: IVRKeyMappingRowProps): ReactElement {
  const isLanguageSwitchDigit = digit === languageSwitchKey;
  const isEnabled = mapping?.enabled !== false;
  // A switched-off option keeps everything it was given; the fields just stop taking edits
  // so the row reads as inactive rather than as something half-configured.
  const rowDisabled = disabled || isLanguageSwitchDigit || !isEnabled;

  const hoursOptions = useMemo(
    () => [
      { value: ALWAYS_HOURS_VALUE, label: 'Always' },
      ...hoursSets.map((hoursSet) => ({ value: hoursSet.hoursSetId, label: hoursSet.name })),
    ],
    [hoursSets]
  );

  const handleEnabledChange = (enabled: boolean) => {
    if (!mapping) {
      return;
    }

    onChange(digit, { ...mapping, enabled });
  };

  const handleInHoursTargetsChange = (targets: IvrTarget[]) => {
    // Clearing the in-hours chain clears the option: there is no menu entry without one, and
    // an after-hours-only option would be unreachable for half the day.
    if (targets.length === 0) {
      onChange(digit, undefined);
      return;
    }

    onChange(digit, { ...mapping, digit, targets });
  };

  const handleAfterHoursTargetsChange = (afterHoursTargets: IvrTarget[]) => {
    if (!mapping) {
      return;
    }

    onChange(digit, { ...mapping, afterHoursTargets: afterHoursTargets.length ? afterHoursTargets : undefined });
  };

  const handleHoursSetChange = (value: string | null) => {
    if (!mapping) {
      return;
    }

    const hoursSetId = value && value !== ALWAYS_HOURS_VALUE ? (value as IvrHoursSetId) : undefined;

    onChange(digit, {
      ...mapping,
      hoursSetId,
      // After-hours routing is meaningless without a set to close, so drop it rather than
      // leave an orphan that only reappears if the option is scoped again later.
      afterHoursTargets: hoursSetId ? mapping.afterHoursTargets : undefined,
    });
  };

  if (isLanguageSwitchDigit) {
    return (
      <Table.Tr opacity={0.6}>
        <Table.Td>
          <Badge variant="light">{digit}</Badge>
        </Table.Td>
        <Table.Td colSpan={3}>
          <Text size="sm">This digit is reserved for language switching. Change it in Hours &amp; language.</Text>
        </Table.Td>
      </Table.Tr>
    );
  }

  return (
    <Table.Tr opacity={isEnabled ? 1 : 0.55}>
      <Table.Td>
        <Stack gap={6} align="flex-start">
          <Badge variant="light">{digit}</Badge>
          <Switch
            size="xs"
            checked={isEnabled}
            disabled={disabled || !mapping}
            aria-label={`Enable digit ${digit}`}
            onChange={(event) => handleEnabledChange(event.currentTarget.checked)}
          />
        </Stack>
      </Table.Td>
      <Table.Td>
        <Select
          data={hoursOptions}
          value={mapping?.hoursSetId ?? ALWAYS_HOURS_VALUE}
          disabled={rowDisabled || !mapping}
          onChange={handleHoursSetChange}
          placeholder="Always"
        />
      </Table.Td>
      <Table.Td>
        <RungChain
          targets={mapping?.targets ?? []}
          disabled={rowDisabled}
          onTargetsChange={handleInHoursTargetsChange}
        />
      </Table.Td>
      <Table.Td>
        {mapping?.hoursSetId ? (
          <RungChain
            targets={mapping.afterHoursTargets ?? []}
            disabled={rowDisabled}
            onTargetsChange={handleAfterHoursTargetsChange}
          />
        ) : (
          <Text size="sm" c="dimmed">
            {mapping ? 'Routes the same around the clock' : '—'}
          </Text>
        )}
      </Table.Td>
    </Table.Tr>
  );
}
